<template>
  <div class="ucn-back" @click.self="$emit('close')">
    <div class="ucn" role="dialog" aria-modal="true" :aria-label="unit.Name + ' card'" :style="{ '--tc': side.bright }">
      <div class="ucn-bar">
        <span class="ucn-kicker">{{ side.label }} team</span>
        <button class="ucn-btn" @click="copy(code(), 'code')" v-tip="'Copy this unit in stream format'">{{ copied === 'code' ? 'Copied ✓' : 'Copy code' }}</button>
        <button v-if="unit.Name" class="ucn-btn" @click="openInBuilder" v-tip="'Open a copy of this unit in the Builder (new tab)'">Builder</button>
        <button class="ucn-btn" :class="{ on: tuneOpen }" :aria-expanded="String(tuneOpen)" @click="tuneOpen = !tuneOpen" v-tip="'Try other Brave / Faith, a removed weapon, or other gear'">Tune</button>
        <button class="ucn-btn ucn-x" @click="$emit('close')" aria-label="Close">✕</button>
      </div>

      <div class="ucn-top">
        <div class="ucn-portrait">
          <UnitSprite :unit="unit" :team-color="side.pal" :fallback-src="fallback" :animated="true" :size="104" />
        </div>
        <div class="ucn-id">
          <div class="ucn-name">{{ unit.Name }} <span class="ucn-grade" :style="{ background: grade.color }" v-tip="'Grade ' + grade.grade + ' · expected win rate ~' + grade.score + '%'">{{ grade.grade }}</span></div>
          <div class="ucn-sub">{{ unit.Gender === 'Monster' ? 'Monster' : unit.Gender }} {{ unit.Class }} · {{ unit.Sign }}<span v-if="unit.RaidBoss" class="ucn-boss">Raid boss</span></div>
          <div class="ucn-zod" v-if="zodiac.length">
            <span v-for="z in zodiac" :key="z.name" class="ucn-zchip" :class="z.c.toLowerCase()">{{ z.c }} vs {{ z.name }}</span>
          </div>
          <div class="ucn-drivers" v-if="drivers.up.length || drivers.down.length">
            <span v-for="p in drivers.up" :key="'u' + p.label" class="ucn-chip up">{{ p.label }} +{{ p.contribution }}</span>
            <span v-for="p in drivers.down" :key="'d' + p.label" class="ucn-chip down">{{ p.label }} {{ p.contribution }}</span>
          </div>
        </div>
        <EvadeGrid :ev="info.evade" :sources="info.sources" size="lg" />
      </div>

      <!-- tuning: every number below follows these (they edit a copy of the unit) -->
      <div v-if="tuneOpen" class="ucn-tune">
        <label class="ucn-slider"><span>Brave <b>{{ eff.Brave }}</b></span><input type="range" min="0" max="100" :value="eff.Brave" @input="tune.brave = +$event.target.value"></label>
        <label class="ucn-slider"><span>Faith <b>{{ eff.Faith }}</b></span><input type="range" min="0" max="100" :value="eff.Faith" @input="tune.faith = +$event.target.value"></label>
        <label v-if="weaponName" class="ucn-check"><input type="checkbox" v-model="tune.disarm"> Weapon removed (broken or stolen)</label>
        <label v-if="!monster" class="ucn-check"><input type="checkbox" v-model="editGear"> Edit gear</label>
        <label v-if="editGear" class="ucn-check" v-tip="'Offer every item, ignoring what this class can equip'"><input type="checkbox" v-model="showAll"> Any item, not just class-legal</label>
      </div>
      <div v-if="edited" class="ucn-edited">
        Edited: {{ editedSummary }}. Every number on this card follows it.
        <button class="ucn-btn" @click="resetTune">Reset</button>
      </div>

      <div class="ucn-stats">
        <div v-for="s in statTiles" :key="s.k" class="ucn-tile" :class="{ changed: s.changed }"><span>{{ s.k }}</span><b>{{ s.v }}</b></div>
      </div>

      <div class="ucn-cols">
        <section class="ucn-sec">
          <h3>Gear</h3>
          <component :is="editGear ? 'button' : 'div'" v-for="g in gear" :key="g.slot" class="ucn-gear" :class="{ editable: editGear, changed: g.key in gearOv }"
                     @click="editGear && (pickerSlot = g.key)" v-tip="editGear ? 'Choose the ' + g.slot.toLowerCase() : null">
            <img v-if="g.name" :src="g.icon" :alt="g.name" @error="$event.target.style.visibility = 'hidden'">
            <span v-else class="ucn-empty"></span>
            <span class="ucn-gear-main"><b>{{ g.name || '—' }}</b><i>{{ g.slot }}{{ g.line ? ' · ' + g.line : '' }}</i></span>
            <span v-if="editGear" class="ucn-gear-edit">Change</span>
          </component>
          <div v-if="elementChips.length" class="ucn-els">
            <span v-for="e in elementChips" :key="e.k + e.el" class="ucn-el" :class="e.k">{{ e.label }} {{ e.el }}</span>
          </div>
        </section>
        <section class="ucn-sec">
          <h3>Skills</h3>
          <div v-for="set in skillSets" :key="set.title" class="ucn-set">
            <div class="ucn-set-title">{{ set.title }}</div>
            <span v-for="sk in set.skills" :key="sk.name" class="ucn-skill" :style="sk.tier ? { borderColor: sk.tier.color } : {}" v-tip="sk.beast ? 'Needs a Beastmaster ally' : sk.tier ? sk.tier.label + ' · rolls ' + sk.roll + '%' : ''">
              {{ sk.name }}<i v-if="sk.tier" :style="{ color: sk.tier.color }"> {{ sk.roll }}%</i>
            </span>
          </div>
          <div class="ucn-rsm">
            <div v-for="r in rsm" :key="r.k"><span>{{ r.k }}</span><b>{{ r.v || '—' }}</b></div>
          </div>
          <div v-if="innates.length" class="ucn-innate">Innate: {{ innates.join(' · ') }}</div>
        </section>
      </div>

      <UnitVsPanel v-if="foe" :unit="eff" :side="effSide" :foe="foe" :gd="gd" :fallback="fallback" :bare="true" />
    </div>
    <GearPickerModal v-if="pickerSlot" :slot="slotLabel(pickerSlot)" :groups="pickerGroups" :value="eff[pickerSlot] || ''"
                     :show-all="showAll" :unlockable="true" :itemschanged="gd.itemschanged"
                     @pick="onPickGear" @close="pickerSlot = null" @toggle-showall="showAll = !showAll" />
  </div>
</template>

<script>
// The full unit card (the 2026-10 refresh; it replaced UnitCardModal on the homepage): identity and grade with what
// drives it, zodiac against the foes, evasion by direction, a stat strip, gear with icons and what each piece adds,
// skills with their roll rarity, and the unit's options against the other team (UnitVsPanel's matrix). Tune tries
// other Brave / Faith, a removed weapon, or other gear (class-legal, via GearPickerModal): the card works on an edited
// copy of the unit (`eff`), so every number follows, the grade, the matrix and Copy code / Builder included.
import UnitSprite from '../UnitSprite.vue';
import EvadeGrid from './EvadeGrid.vue';
import UnitVsPanel from './UnitVsPanel.vue';
import modalA11y from '../../mixins/modalA11y.js';
import { unitInfo, gearRows, elementChips, skillSets } from './unitInfo.js';
import { unitGrade, topParts } from '../../lib/unitGrade.js';
import { rosterStats } from '../../lib/preload.js';
import { zodiacCompat } from '../../lib/zodiac.js';
import { encodeBuild, exportUnit } from '../../lib/streamFormat.js';
import { classGearRecord, slotItems } from '../../lib/classGear.js';
import GearPickerModal from '../GearPickerModal.vue';

const SLOT_LABEL = { Mainhand: 'Right hand', Offhand: 'Left hand', Head: 'Head', Armor: 'Body', Accessory: 'Accessory' };

export default {
  name: 'UnitCardNew',
  components: { UnitSprite, EvadeGrid, UnitVsPanel, GearPickerModal },
  mixins: [modalA11y],
  props: {
    unit: { type: Object, required: true },
    side: { type: Object, required: true },
    foe: { type: Object, default: null },
    gd: { type: Object, required: true },
    fallback: { type: String, default: '' },
  },
  emits: ['close'],
  data() { return { copied: '', tuneOpen: false, tune: { brave: null, faith: null, disarm: false }, gearOv: {}, editGear: false, showAll: false, pickerSlot: null }; },
  watch: { unit() { this.resetTune(); this.tuneOpen = false; } },
  computed: {
    monster() { return this.unit.Gender === 'Monster'; },
    // the unit as tuned: Brave / Faith, gear swaps, and the weapon removed
    eff() {
      const t = this.tune, u = { ...this.unit, ...this.gearOv };
      if (t.brave != null) u.Brave = t.brave;
      if (t.faith != null) u.Faith = t.faith;
      if (t.disarm) u.Mainhand = '';
      return u;
    },
    // the unit's side with the tuned copy in its place (the matrix finds the unit in its own roster)
    effSide() { return { ...this.side, units: this.side.units.map((x) => (x === this.unit ? this.eff : x)) }; },
    weaponName() { return this.gearOv.Mainhand ?? this.unit.Mainhand; },
    edited() { const t = this.tune; return t.brave != null || t.faith != null || t.disarm || Object.keys(this.gearOv).length > 0; },
    editedSummary() {
      const t = this.tune, u = this.unit, out = [];
      if (t.brave != null && t.brave !== u.Brave) out.push(`Brave ${u.Brave} → ${t.brave}`);
      if (t.faith != null && t.faith !== u.Faith) out.push(`Faith ${u.Faith} → ${t.faith}`);
      if (t.disarm) out.push('weapon removed');
      for (const [k, v] of Object.entries(this.gearOv)) out.push(`${SLOT_LABEL[k].toLowerCase()}: ${u[k] || 'empty'} → ${v || 'empty'}`);
      return out.join(', ') || 'back to its own values';
    },
    info() { return unitInfo(this.eff, this.gd); },
    baseInfo() { return this.edited ? unitInfo(this.unit, this.gd) : this.info; },
    grade() { return unitGrade(this.eff, rosterStats, this.gd); },
    drivers() { return topParts(this.grade.parts || [], 3); },
    statTiles() {
      const s = this.info.stats, b = this.baseInfo.stats, u = this.eff, o = this.unit;
      return [['HP', 'hp'], ['MP', 'mp'], ['Move', 'move'], ['Jump', 'jump'], ['Speed', 'speed'], ['PA', 'pa'], ['MA', 'ma']].map(([k, f]) => ({ k, v: s[f], changed: s[f] !== b[f] }))
        .concat([{ k: 'Brave', v: u.Brave, changed: u.Brave !== o.Brave }, { k: 'Faith', v: u.Faith, changed: u.Faith !== o.Faith }]);
    },
    zodiac() {
      if (!this.foe) return [];
      return this.foe.units.map((f) => ({ name: f.Name, c: zodiacCompat(this.unit.Gender, this.unit.Sign, f.Gender, f.Sign) })).filter((z) => z.c && z.c !== 'Neutral');
    },
    gear() { return gearRows(this.eff, this.gd, this.editGear); },
    elementChips() { return elementChips(this.info.elements); },
    skillSets() { return skillSets(this.unit, this.gd); },
    rsm() { const u = this.unit; return [{ k: 'Reaction', v: u.ReactionSkill }, { k: 'Support', v: u.SupportSkill }, { k: 'Move', v: u.MoveSkill }]; },
    innates() { return ((this.info.classBase && this.info.classBase.innates) || []).map((i) => i && i.name).filter(Boolean); },
    gearRec() { return classGearRecord(this.unit.Class, this.unit.Gender); },
    pickerGroups() { return this.pickerSlot ? slotItems(this.pickerSlot, this.gearRec, this.unit.SupportSkill, this.showAll) : []; },
  },
  methods: {
    resetTune() { this.tune = { brave: null, faith: null, disarm: false }; this.gearOv = {}; this.editGear = false; this.showAll = false; this.pickerSlot = null; },
    slotLabel(k) { return SLOT_LABEL[k] || k; },
    onPickGear(name) {
      const k = this.pickerSlot;
      if (k) {
        const ov = { ...this.gearOv };
        if (name === (this.unit[k] || '')) delete ov[k]; else ov[k] = name;
        this.gearOv = ov;
        if (k === 'Mainhand') this.tune.disarm = false;
      }
      this.pickerSlot = null;
    },
    // the same two as the old card (UnitCardModal): the stream-format code, and a copy of the unit in the Builder
    code() { return exportUnit(this.eff, ''); },
    copy(text, what) {
      const done = () => { this.copied = what; window.setTimeout(() => { if (this.copied === what) this.copied = ''; }, 1500); };
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch (e) { /* ignore */ }
        document.body.removeChild(ta); done();
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, fallback);
      else fallback();
    },
    openInBuilder() {
      window.open(window.location.origin + window.location.pathname + '?build&u=' + encodeBuild(this.eff, this.unit.Name), '_blank', 'noopener');
    },
  },
};
</script>

<style scoped>
.ucn-back { position: fixed; inset: 0; z-index: 1000; display: grid; place-items: start center; overflow-y: auto; padding: 40px 12px; background: rgba(4,6,12,.72); }
.ucn { --brass: #d9b45e; --brass-hi: #f3d68e; --ink: #cdd3dd; --mute: #8b93a3; --cond: 'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif; --display: 'Cinzel', Georgia, serif;
  position: relative; width: min(980px, 100%); color: var(--ink); border: 1px solid #7a5f2e; border-top: 4px solid var(--tc); border-radius: 6px; box-shadow: 0 16px 50px rgba(0,0,0,.6);
  background:
    linear-gradient(#e8cf8c, #e8cf8c) left 0 bottom 0 / 14px 2px no-repeat, linear-gradient(#e8cf8c, #e8cf8c) left 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(#e8cf8c, #e8cf8c) right 0 bottom 0 / 14px 2px no-repeat, linear-gradient(#e8cf8c, #e8cf8c) right 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(180deg, #1b2131, #0e121c); }
.ucn-bar { display: flex; align-items: center; gap: 8px; padding: 8px 14px 0; }
.ucn-kicker { font-family: var(--cond); font-weight: 700; font-size: 12px; letter-spacing: 2px; text-transform: uppercase; color: var(--tc); margin-right: auto; filter: brightness(1.4); }
.ucn-btn { font-family: var(--cond); font-weight: 600; font-size: 13px; letter-spacing: .5px; text-transform: uppercase; background: transparent; color: var(--brass); border: 1px solid #7a5f2e; border-radius: 3px; padding: 2px 9px; cursor: pointer; }
.ucn-btn:hover { background: rgba(217,180,94,.12); }
.ucn-x { color: var(--ink); border-color: #3a4560; }
.ucn-btn.on { background: rgba(217,180,94,.18); color: var(--brass-hi); }
.ucn-tune { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 20px; margin: 0 16px 8px; padding: 8px 12px; border: 1px solid rgba(217,180,94,.35); border-radius: 4px; background: rgba(0,0,0,.2); font-size: 13.5px; }
.ucn-slider { display: flex; align-items: center; gap: 8px; }
.ucn-slider span { min-width: 72px; font-family: var(--cond); font-weight: 600; letter-spacing: .5px; text-transform: uppercase; color: var(--brass); }
.ucn-slider b { color: #f6eedb; font-variant-numeric: tabular-nums; }
.ucn-slider input { width: 150px; accent-color: var(--brass); }
.ucn-check { display: flex; align-items: center; gap: 6px; cursor: pointer; }
.ucn-check input { accent-color: var(--brass); }
.ucn-edited { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin: 0 16px 8px; padding: 5px 10px; border-left: 3px solid var(--brass); background: rgba(217,180,94,.08); font-size: 13px; color: var(--brass-hi); }
.ucn-tile.changed b { color: var(--brass-hi); text-decoration: underline dotted; text-underline-offset: 3px; }
.ucn-gear.changed .ucn-gear-main b { color: var(--brass-hi); }
.ucn-top { display: flex; gap: 16px; align-items: center; padding: 6px 16px 10px; flex-wrap: wrap; }
.ucn-portrait { width: 116px; height: 116px; display: grid; place-items: center; border-radius: 50%;
  background: radial-gradient(circle at 50% 60%, color-mix(in srgb, var(--tc) 45%, transparent), transparent 70%); }
.ucn-id { flex: 1; min-width: 260px; }
.ucn-name { font-family: var(--display); font-weight: 700; font-size: 28px; line-height: 1.1; color: #f6eedb; }
.ucn-grade { display: inline-block; margin-left: 8px; padding: 0 9px; border-radius: 3px; font-family: var(--cond); font-size: 18px; color: #fff; text-shadow: 0 1px 0 rgba(0,0,0,.5); vertical-align: 4px; }
.ucn-sub { font-size: 14px; color: var(--mute); margin-top: 2px; }
.ucn-boss { margin-left: 8px; padding: 0 6px; border-radius: 3px; background: #7a1f1f; color: #ffd9d0; font-size: 12px; }
.ucn-zod, .ucn-drivers { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px; }
.ucn-zchip, .ucn-chip { padding: 1px 8px; border-radius: 10px; font-size: 12px; }
.ucn-zchip.best, .ucn-zchip.good { background: rgba(80,170,90,.2); color: #9fe0a6; }
.ucn-zchip.bad, .ucn-zchip.worst { background: rgba(200,80,70,.2); color: #f0a49b; }
.ucn-chip.up { background: rgba(80,170,90,.14); color: #9fe0a6; } .ucn-chip.down { background: rgba(200,80,70,.14); color: #f0a49b; }
.ucn-stats { display: grid; grid-template-columns: repeat(9, minmax(0, 1fr)); gap: 1px; margin: 0 16px; background: rgba(217,180,94,.25); border: 1px solid rgba(217,180,94,.25); border-radius: 4px; overflow: hidden; }
@media (max-width: 640px) { .ucn-stats { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
.ucn-tile { display: flex; flex-direction: column; align-items: center; padding: 4px 0; background: #121826; }
.ucn-tile span { font-family: var(--cond); font-weight: 600; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--brass); }
.ucn-tile b { font-family: var(--cond); font-weight: 700; font-size: 20px; color: #f6eedb; font-variant-numeric: tabular-nums; }
.ucn-cols { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; padding: 12px 16px; }
@media (max-width: 720px) { .ucn-cols { grid-template-columns: minmax(0, 1fr); } }
.ucn-sec h3 { margin: 0 0 6px; font-family: var(--display); font-size: 15px; color: var(--brass-hi); border-bottom: 1px solid rgba(217,180,94,.3); padding-bottom: 2px; }
.ucn-gear { display: flex; gap: 8px; align-items: center; padding: 3px 0; }
button.ucn-gear { width: 100%; padding: 3px 6px; margin: 0 -6px; background: none; border: 1px solid transparent; border-radius: 3px; font: inherit; color: inherit; text-align: left; cursor: pointer; }
button.ucn-gear:hover { border-color: rgba(217,180,94,.4); background: rgba(217,180,94,.06); }
.ucn-gear-edit { margin-left: auto; font-family: var(--cond); font-size: 12px; letter-spacing: .6px; text-transform: uppercase; color: var(--brass); }
.ucn-gear img, .ucn-empty { width: 24px; height: 24px; image-rendering: pixelated; flex: none; }
.ucn-gear-main { display: flex; flex-direction: column; min-width: 0; }
.ucn-gear-main b { font-family: var(--cond); font-weight: 600; font-size: 15px; color: #f3ecd8; }
.ucn-gear-main i { font-style: normal; font-size: 12px; color: var(--mute); }
.ucn-els { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px; }
.ucn-el { padding: 1px 8px; border-radius: 10px; font-size: 12px; background: rgba(255,255,255,.08); }
.ucn-el.absorb, .ucn-el.cancel, .ucn-el.half { color: #9fe0a6; } .ucn-el.weak { color: #f0a49b; } .ucn-el.strengthen { color: #f3d68e; }
.ucn-set { margin-bottom: 8px; }
.ucn-set-title { font-family: var(--cond); font-weight: 700; font-size: 12px; letter-spacing: 1.4px; text-transform: uppercase; color: var(--mute); margin-bottom: 3px; }
.ucn-skill { display: inline-block; margin: 0 4px 4px 0; padding: 1px 8px; border: 1px solid #3a4560; border-radius: 3px; font-size: 13px; color: #f3ecd8; }
.ucn-skill i { font-style: normal; font-size: 11.5px; }
.ucn-rsm { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; margin-top: 4px; }
.ucn-rsm span { display: block; font-family: var(--cond); font-weight: 600; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--brass); }
.ucn-rsm b { font-weight: 500; font-size: 13.5px; color: #f3ecd8; }
.ucn-innate { margin-top: 6px; font-size: 12px; color: var(--mute); }
</style>
